import config from 'config';
import debug from 'debug';
import * as commonSelectors from '~/domains/common/selectors';
import * as authenticationService from '~/services/authenticationService';
import constants from '~/utils/constants';
import { fetch } from '~/utils/fetch';
import * as analyticsActions from '~/domains/analytics/actions';
import { EVENT_TYPES } from '~/analytics/events';
import { openPopup } from '~/utils/popup';
import { closeModal } from '~/domains/modal/actions';
import { generatePKCEPair } from '~/utils/pkce';
import * as actions from './actionTypes';
import * as selectors from './selectors';

const log = debug('exchange:relogin');

const PKCE_VERIFIER_KEY = 'exchange.relogin.pkce_verifier';

export function cleanPendingActions() {
  return {
    type: actions.CLEAN_PENDING_ACTIONS
  };
}

export function executePendingActions() {
  return (dispatch, getState) => {
    const state = getState();
    const pendingActions = selectors.pendingActions(state);

    log(`dispatching ${pendingActions.length} pending actions`);
    dispatch(cleanPendingActions());
    pendingActions.forEach((action) => action());
  };
}

export function addPendingAction(action) {
  return {
    type: actions.ADD_PENDING_ACTION,
    action
  };
}

/**
 * Re-authenticate the user via a popup after a session timeout, using the
 * OAuth Authorization Code + PKCE flow (replacing the old implicit grant
 * which exposed the access token in the popup URL fragment).
 *
 * Flow:
 *   1. Generate a PKCE verifier+challenge and stash the verifier in
 *      sessionStorage so the popup callback (running in this same window
 *      context) can retrieve it.
 *   2. Open the popup at cs's /oauth2/authorize with the challenge.
 *   3. Popup redirects back to /oauth.html with `?code=...`, which calls
 *      window.authenticateOAuth(href).
 *   4. POST {code, codeVerifier} to exchange-ui's server-side
 *      /oauth/pkce-token route, which forwards to cs over mTLS
 *      using our certificate. We do not call cs's /oauth2/token directly
 *      from the browser because the `exchange2_implicit` ARC client is
 *      configured with `token_endpoint_auth_method: 'tls_client_auth'`.
 *   5. Resume the pending actions with the new access token.
 *
 * Related: W-22351940
 */
export function reloginUser({ isPublicPortal, setToken }) {
  return async (dispatch, getState) => {
    const state = getState();
    const profile = commonSelectors.profile(state);
    const organizationId = profile.organization.id;

    const { codeVerifier, codeChallenge } = await generatePKCEPair();

    sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

    const loginURL = authenticationService.getClientAuthenticationURL({
      isPublicPortal,
      redirectURL: constants.oauthCallbackUrl,
      domain: profile.organization.domain,
      codeChallenge
    });

    window.authenticateOAuth = async (responseUrl) => {
      try {
        const url = new URL(responseUrl);
        const code = url.searchParams.get('code');

        if (!code) {
          log('No authorization code received');

          return;
        }

        const storedCodeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

        if (!storedCodeVerifier) {
          log('No PKCE verifier found in sessionStorage');

          return;
        }

        const tokenResponse = await fetch(
          `${config.contextPath}/oauth/pkce-token`,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ code, codeVerifier: storedCodeVerifier })
          }
        );

        if (!tokenResponse.ok) {
          log('Token exchange failed', tokenResponse.status);
          // Don't leave the session-expired modal stranded with no feedback;
          // close it so the user can retry the action that triggered relogin.
          dispatch(closeModal());

          return;
        }

        const { accessToken, tokenType } = await tokenResponse.json();

        setToken(accessToken, tokenType);

        dispatch(
          analyticsActions.trackEvent(EVENT_TYPES.USER_RELOGGED_IN, {
            organizationId
          })
        );
        dispatch(closeModal());
        dispatch(executePendingActions());
      } catch (err) {
        log('Failed to complete relogin token exchange', err);
        // On a network/parse failure, close the modal too rather than
        // leaving the user stuck on an unresponsive popup-relogin modal.
        dispatch(closeModal());
      } finally {
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);
      }
    };

    openPopup(loginURL, 'Login', 400, 400);
  };
}
